if (!customElements.get('custom-tp-transformation-slider')) {
  customElements.define(
    'custom-tp-transformation-slider',
    class CustomTpTransformationSlider extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-tp-transformation-slider]');
        this.originalCount = Number(this.slider?.dataset.originalCount) || 0;
        this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');

        this.onSectionLoad = this.onSectionLoad.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);

        document.addEventListener('shopify:section:load', this.onSectionLoad);
        this.mobileMediaQuery.addEventListener('change', this.onMediaChange);

        this.initTrustpilotSummary();
        this.initSlider();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        this.mobileMediaQuery.removeEventListener('change', this.onMediaChange);
        this.destroySlider();
      }

      onSectionLoad(event) {
        if (event.target?.contains(this)) {
          this.loadTrustpilotSummary();
          this.destroySlider();
          this.initSlider();
        }
      }

      onMediaChange() {
        this.destroySlider();
        this.initSlider();
      }

      initTrustpilotSummary() {
        this.loadTrustpilotSummary();
      }

      async loadTrustpilotSummary() {
        const businessUnitId = this.dataset.trustpilotBusinessUnitId;
        const templateId = this.dataset.trustpilotTemplateId;
        const token = this.dataset.trustpilotToken;
        const locale = this.dataset.trustpilotLocale || 'en-US';

        if (!businessUnitId || !templateId || !token) {
          return;
        }

        const params = new URLSearchParams({
          businessUnitId,
          locale,
          theme: 'light',
          token,
        });

        try {
          const response = await fetch(
            `https://widget.trustpilot.com/trustbox-data/${templateId}?${params.toString()}`
          );

          if (!response.ok) {
            return;
          }

          const data = await response.json();
          this.updateTrustpilotSummary(data);
        } catch {
          return;
        }
      }

      updateTrustpilotSummary(data) {
        const scoreElement = this.querySelector('[data-trustpilot-score]');
        const reviewCountElement = this.querySelector('[data-trustpilot-review-count]');
        const ratingLinkElement = this.querySelector('[data-trustpilot-rating-link]');
        const reviewsLinkElement = this.querySelector('[data-trustpilot-reviews-link]');
        const totalReviews = data?.businessEntity?.numberOfReviews?.total;
        const scoreLabel = data?.starsString;
        const syncScore = this.dataset.trustpilotSyncScore === 'true';

        if (scoreElement && scoreLabel && syncScore) {
          scoreElement.textContent = scoreLabel;
        }

        if (reviewCountElement && totalReviews != null) {
          const locale = this.dataset.trustpilotLocale || 'en-US';
          reviewCountElement.textContent = new Intl.NumberFormat(locale).format(totalReviews);
        }

        if (ratingLinkElement && scoreElement) {
          ratingLinkElement.setAttribute(
            'aria-label',
            `Trustpilot rating: ${scoreElement.textContent}`
          );
        }

        if (reviewsLinkElement && reviewCountElement) {
          const reviewsMeta = reviewCountElement.closest('.custom-results-trustpilot-cards__trustpilot-meta');
          const reviewsLabel = reviewsMeta?.textContent?.trim() || reviewCountElement.textContent;

          reviewsLinkElement.setAttribute(
            'aria-label',
            `Read ${reviewsLabel} on Trustpilot`
          );
        }
      }

      async initSlider() {
        if (!this.slider || !this.mobileMediaQuery.matches || this.originalCount === 0) {
          return;
        }

        if (this.slider.swiper) {
          return;
        }

        this.previousButton = this.querySelector('[data-tp-transformation-prev]');
        this.nextButton = this.querySelector('[data-tp-transformation-next]');
        this.pagination = this.querySelector('[data-tp-transformation-dots]');

        await this.waitForSwiper();

        this.swiper = new Swiper(this.slider, {
          slidesPerView: 'auto',
          spaceBetween: 9,
          loop: true,
          centeredSlides: true,
          allowTouchMove: true,
          grabCursor: true,
          speed: 500,
          navigation: {
            nextEl: this.nextButton,
            prevEl: this.previousButton,
          },
          pagination: {
            el: this.pagination,
            type: 'bullets',
            clickable: true,
            bulletClass: 'custom-tp-transformation-slider__dot',
            bulletActiveClass: 'is-active',
            renderBullet: (index, className) => {
              if (this.originalCount > 0 && index >= this.originalCount) {
                return `<button class="${className}" type="button" tabindex="-1" aria-hidden="true" style="display:none"></button>`;
              }

              return `<button class="${className}" type="button" aria-label="Show result ${index + 1}"></button>`;
            },
          },
          on: {
            slideChange: (swiper) => {
              if (!this.originalCount || !this.pagination) {
                return;
              }

              const realIndex = swiper.realIndex % this.originalCount;
              const bullets = this.pagination.querySelectorAll('.custom-tp-transformation-slider__dot');

              bullets.forEach((bullet, index) => {
                bullet.classList.toggle('is-active', index === realIndex);
              });
            },
          },
        });
      }

      destroySlider() {
        this.swiper?.destroy?.();
        this.swiper = null;
      }

      waitForSwiper() {
        return new Promise((resolve) => {
          if (window.Swiper) {
            resolve();
            return;
          }

          const checkSwiper = () => {
            if (window.Swiper) {
              resolve();
              return;
            }

            window.setTimeout(checkSwiper, 50);
          };

          checkSwiper();
        });
      }
    }
  );
}
